import React, { useEffect, useState } from 'react'
import { Label, Loader, Message, Modal, Table } from 'semantic-ui-react'
import { X } from 'phosphor-react'
import styled from 'styled-components'
import { FlexRowBetween, TitleText } from 'components'
import { MaintenanceService } from 'utility'
import { formatDate, maintenanceApiError } from './maintenance-utils'

const Strip = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: -8px 0 16px;
    .chip {
        cursor: pointer;
    }
`

// Counts of the caller's maintenance work (GET Maintenance/Summary) -> list (GET Maintenance/Tasks?filter=) -> task detail.
const CHIPS = [
    { key: 'AwaitingMyApproval', filter: 'awaiting_my_approval', text: 'Awaiting my approval', color: 'orange' },
    { key: 'AwaitingMyVerification', filter: 'awaiting_my_verification', text: 'Verifications to do', color: 'blue' },
    { key: 'MyOverdue', filter: 'overdue', text: 'My overdue maintenance', color: 'red' },
    { key: 'MyOpen', filter: 'mine', text: 'My open maintenance', color: 'teal' },
]

/**
 * Maintenance alert strip for the task list (EAUDITOR-409 step 3). Hidden when nothing is waiting or maintenance is not
 * installed. Props: onOpenTask(taskId), refreshKey (reload after changes).
 */
export const MaintenanceAlertStrip = ({ onOpenTask, refreshKey }) => {
    const [summary, setSummary] = useState(null)
    const [list, setList] = useState(null) // { chip, rows, loading, error }

    useEffect(() => {
        let alive = true
        MaintenanceService.summary()
            .then(result => alive && setSummary(result || null))
            .catch(() => alive && setSummary(null))
        return () => {
            alive = false
        }
    }, [refreshKey])

    const openList = chip => {
        setList({ chip, rows: [], loading: true, error: '' })
        MaintenanceService.myTasks(chip.filter)
            .then(rows => setList(prev => (prev?.chip === chip ? { ...prev, rows: Array.isArray(rows) ? rows : [], loading: false } : prev)))
            .catch(error => setList(prev => (prev?.chip === chip ? { ...prev, loading: false, error: maintenanceApiError(error) } : prev)))
    }

    const chips = summary ? CHIPS.filter(c => Number(summary[c.key]) > 0) : []
    if (!chips.length && !list) return null

    return (
        <>
            {chips.length > 0 && (
                <Strip>
                    {chips.map(c => (
                        <Label key={c.key} className="chip" color={c.color} basic onClick={() => openList(c)}>
                            {c.text}
                            <Label.Detail>{summary[c.key]}</Label.Detail>
                        </Label>
                    ))}
                </Strip>
            )}
            <Modal open={!!list} onClose={() => setList(null)} size="large">
                <Modal.Header>
                    <FlexRowBetween>
                        <TitleText>{list?.chip?.text || 'Maintenance tasks'}</TitleText>
                        <X size={20} className="pointer" onClick={() => setList(null)} />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content scrolling>
                    {list?.loading && <Loader active inline="centered" />}
                    {list?.error && <Message negative content={list.error} />}
                    {list && !list.loading && !list.error && !list.rows.length && <Message info content="Nothing here any more." />}
                    {list && list.rows.length > 0 && (
                        <Table compact celled selectable unstackable>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Task</Table.HeaderCell>
                                    <Table.HeaderCell>Asset</Table.HeaderCell>
                                    <Table.HeaderCell>Stage</Table.HeaderCell>
                                    <Table.HeaderCell>Due</Table.HeaderCell>
                                    <Table.HeaderCell>Assigned to</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {list.rows.map(r => (
                                    <Table.Row
                                        key={r.TaskId}
                                        className="pointer"
                                        onClick={() => {
                                            setList(null)
                                            onOpenTask(r.TaskId)
                                        }}
                                    >
                                        <Table.Cell>{r.Title}</Table.Cell>
                                        <Table.Cell>{r.AssetName || '-'}</Table.Cell>
                                        <Table.Cell>{r.StageName || '-'}</Table.Cell>
                                        <Table.Cell>{formatDate(r.TaskDue)}</Table.Cell>
                                        <Table.Cell>{r.AssigneeName || '-'}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    )}
                </Modal.Content>
            </Modal>
        </>
    )
}
